import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import { NO_EXECUTE_CONTENTFULL_ROUTES } from 'utils/utils'

/**
 * Hook para obtener métricas de Contentful
 * Incluye datos para SDK y placeholders del buscador
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulMetrics = () => {
	const pathname = window.location.pathname
	const isExceptionRoute = NO_EXECUTE_CONTENTFULL_ROUTES.includes(pathname)
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'metrics'] as const,
		queryFn: queriesApi.getMetrics,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		enabled: !isExceptionRoute
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Metrics desde Contentful:', error)
	}

	// Procesar lista de SDK
	const listaSdk = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return rawData?.homeCollection?.items?.[0]?.listaSdk || []
		} catch (transformError) {
			console.error('Error al procesar lista de SDK:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Procesar placeholders del buscador
	const placeholdersBuscador = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return rawData?.homeCollection?.items?.[0]?.placeholdersBuscador || []
		} catch (transformError) {
			console.error('Error al procesar placeholders del buscador:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		listaSdk,
		placeholdersBuscador,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulMetrics
